// SPDX-License-Identifier: MIT
// The Q&A advance loop for the folded deep-discovery (guided first-run beat ④).
// Owns the conversation: appends each file-grounded question one at a time; on an
// answer it acks + autosaves (onSaveDraft) + advances; and once every question is
// answered the « 💬 Régénérer avec mes réponses » payoff finishes the loop
// (onFinish → buildRefinementContext → magnify → saveProjectVersion('discovery')).
// The pure sequencing helper (firstUnansweredIndex) is unit-tested; the mount
// wires the proven welcome.ts discovery callbacks to the renderFoldedDiscovery
// view. Append-only by design (the chat metaphor) — to change a prior answer the
// user uses « 🔄 Refaire la découverte ». Guidance is additive; any callback that
// throws is logged non-fatally and never blocks the surrounding flow.
import { t } from '../i18n/t';
import { renderFoldedDiscovery, type FoldedDiscoveryElement } from '../components/folded-discovery';
import type { DiscoveryQuestion, DiscoveryResult } from '../projects/discover';

export interface FoldedDiscoveryDriverOptions {
  discovery: DiscoveryResult;
  initialAnswers?: Record<string, string>;
  onSaveDraft(answers: Record<string, string>): Promise<void>;
  onFinish(answers: Record<string, string>): Promise<void>;
  onRestart(): void | Promise<void>;
}

// Index of the first question with no (non-blank) answer; === questions.length
// when every question is answered. Pure → unit-testable.
export function firstUnansweredIndex(
  questions: DiscoveryQuestion[],
  answers: Record<string, string>,
): number {
  for (let i = 0; i < questions.length; i++) {
    const a = answers[questions[i].id];
    if (!a || !a.trim()) return i;
  }
  return questions.length;
}

export function mountFoldedDiscovery(opts: FoldedDiscoveryDriverOptions): FoldedDiscoveryElement {
  const { discovery } = opts;
  const questions = discovery.questions;
  const total = questions.length;

  // Live answers — seed from prior persisted answers, dropping any blanks so a
  // half-typed prior session restores to the right "first gap".
  const answers: Record<string, string> = {};
  for (const [k, v] of Object.entries(opts.initialAnswers ?? {})) {
    if (v && v.trim()) answers[k] = v;
  }
  let cursor = firstUnansweredIndex(questions, answers);
  const answeredCount = (): number => Object.keys(answers).length;
  // Every captured answer, paired with its question, for the « voir mes réponses »
  // review panel (so an append-only chat never loses what the user typed).
  const reviewItems = (): { q: string; a: string }[] =>
    questions.filter((qq) => answers[qq.id]).map((qq) => ({ q: qq.question, a: answers[qq.id] }));

  let el: FoldedDiscoveryElement;

  const genericSource = t('onboarding.discSource');
  const askCurrent = (): void => {
    if (cursor >= total) {
      el.__setComposerEnabled(false);
      el.__collapseOpenWhy();                       // terminal: no current question → no open bubble
      el.__appendAck(t('onboarding.discAllAnswered'));
      return;
    }
    const q = questions[cursor];
    el.__appendQuestion({
      id: q.id,
      index: cursor + 1,
      total,
      source: q.source?.trim() ? q.source : genericSource, // guards "" AND undefined
      text: q.question,
      why: q.why,                                   // undefined/"" → no bubble
    });
    el.__setComposerEnabled(true);
  };

  el = renderFoldedDiscovery({
    summary: discovery.discovery_summary,
    total,
    answered: answeredCount(),
    skipped: discovery.attachments_skipped,
    onAnswer: (text) => {
      if (cursor >= total) return;
      answers[questions[cursor].id] = text;
      el.__appendAck();
      void opts.onSaveDraft({ ...answers }).catch((e) => console.warn('[folded-discovery] autosave failed (non-fatal)', e));
      cursor = firstUnansweredIndex(questions, answers);
      el.__renderRecap(answeredCount(), total);
      el.__setAnswersRecap(reviewItems());
      askCurrent();
    },
    onRegenerate: () => {
      el.__setComposerEnabled(false);
      void Promise.resolve(opts.onFinish({ ...answers })).catch((e) => {
        console.warn('[folded-discovery] regenerate failed', e);
        el.__setComposerEnabled(cursor < total); // re-open if the user can still answer
      });
    },
    onSaveDraft: () => { void opts.onSaveDraft({ ...answers }).catch((e) => console.warn('[folded-discovery] save failed', e)); },
    onRestart: () => { void Promise.resolve(opts.onRestart()).catch((e) => console.warn('[folded-discovery] restart failed', e)); },
  });

  // Replay already-answered questions as compact "✓ répondue" fold pills, then
  // ask the first unanswered one (the current focus).
  for (let i = 0; i < cursor; i++) {
    el.__appendFold(t('onboarding.discFolded').replace('{n}', String(i + 1)));
  }
  el.__renderRecap(answeredCount(), total);
  el.__setAnswersRecap(reviewItems());
  askCurrent();
  return el;
}
